import { ApiService, ApiError } from './api.service'
import { BookingOption } from './booking.service'
import { Constants } from '../constants'
import moment from 'moment'
import { TimeSlotUtil } from '../utils/timeslot.util'
import { TimezoneService } from "@/services/timezone.service";
import {  LocationService } from "@/services/location.service";
import {KeycloakService} from "@/services/keycloak.service";
import {Spot} from "@/services/group.service";

class TimeSlotLengthDropdown {
    constructor(id, time) {
        this.id = id
        this.value = time.toString()
    }

}

class Entity {
    constructor(entity) {
        this.id = entity ? entity.id : undefined
        this.name = entity ? entity.name : undefined
        this.startDate = entity ? entity.startDate || new Date() : new Date()
        this.endDate = entity ? entity.endDate || new Date() : new Date()
        this.bookings = entity && entity.bookings ? entity.bookings.map(o => new EntityBooking(o)) : []
        this.description = entity ? entity.description : undefined
        this.autoApproveBooking = entity ? entity.autoApproveBooking : false
        this.timeSlotToleranceInMinutes = entity ? entity.timeSlotToleranceInMinutes : undefined
        this.reservationExpirationInMinutes = entity ? entity.reservationExpirationInMinutes : undefined
        this.timeSlotStartTime = entity ? entity.timeSlotStartTime : undefined
        this.timeSlotEndTime = entity ? entity.timeSlotEndTime : undefined
        this.openingHours = entity ? entity.openingHours : undefined
        this.timeSlotLengthInMinutes = entity ? entity.timeSlotLengthInMinutes : undefined
        this.active = entity ? entity.active : false
        this.external = entity ? entity.external : false
        this.upfrontBookingWindowInMinutes = entity ? entity.upfrontBookingWindowInMinutes : undefined
        this.notifyMissingLicensePlate = entity ? entity.notifyMissingLicensePlate : false
        this.overrideDockStatusCheck = entity ? entity.overrideDockStatusCheck : false
        this.bookingOptions = entity && entity.bookingOptions ? entity.bookingOptions.map(o => new BookingOption(o)) || [] : []

        //optional fields
        this.spots = entity?.spots ? entity.spots.map(o => new Spot(o)) : []
    }

    createBookingTimeSlots(availableTimeSlots, timeSlotWidth) {
        for (const booking of this.bookings) {
            booking.createTimeSlots(availableTimeSlots, timeSlotWidth)
        }
    }
}

class EntityOption {
    constructor(id, type, timeSlotLengthFormulas) {
        this.id = id
        this.type = type
        this.timeSlotLengthFormulas = timeSlotLengthFormulas
    }
}

class EntityBooking {
    constructor(booking) {
        this.id = booking ? booking.id : undefined
        this.startDate = booking ? new Date(booking.startDate) : undefined
        this.endDate = booking ? new Date(booking.endDate) : undefined
        this.status = booking ? booking.status : undefined
        this.customer = booking ? booking.customer : undefined
        this.options = booking ? booking.options : [] || []
        // Extensive properties
        this.startTimeSlot = undefined
        this.endTimeSlot = undefined
        this.timeSlotWidth = 0
        this.userCreated = booking ? booking.userCreated : undefined
        this.frontPlate = booking ? booking.licensePlate : undefined
        this.rearPlate = booking ? booking.trailerPlate : undefined
    }

    createTimeSlots(availableTimeSlots, timeSlotWidth) {
        this.startTimeSlot = TimeSlotUtil.createTimeSlotByDate(this.startDate)
        this.endTimeSlot = TimeSlotUtil.createTimeSlotByDate(this.endDate)

        this.startTimeSlot.index = TimeSlotUtil.getMatchingTimeSlotIndex(this.startTimeSlot, availableTimeSlots)
        const end = TimeSlotUtil.getMatchingTimeSlotIndex(this.endTimeSlot, availableTimeSlots)
        this.endTimeSlot.index = end === 0 ? availableTimeSlots.length - 1 : end
        this.timeSlotWidth = (this.endTimeSlot.index - this.startTimeSlot.index) * (Constants.TIMESLOT_WIDTH * timeSlotWidth)  - 1 + 'px'
    }

    getTimeSlotDisplay() {
        const startTime = moment(this.startDate).format('HH:mm')
        const endTime = moment(this.endDate).format('HH:mm')
        return `${startTime} - ${endTime}`
    }

    getArrivalDate() {
        return moment(this.startDate).format('DD-MM-YYYY')
    }

    getVisitsDisplayOptions() {
        const options = this.options ? this.options.filter(option => option.usedForVisitsDisplay) : []
        return options
    }

    getVisitsDisplayOptionsText(selectedOption) {
        const options = this.options ? this.options.filter(option => option.usedForVisitsDisplay) : []
        switch (selectedOption.id) {
            case 100000:
                return options.length == 0 ? "" : options[0].value
            case 100001:
                return this.customer.name
            case 100002:
                return this.frontPlate
            case 100003:
                return this.rearPlate
            default:
                const option = this.options.find(option => option.id === selectedOption.id)
                if (option) {
                    return option.value
                }
                return options.length == 0 ? "" : options[0].value
        }
    }


}

class EntityDropdown {
    constructor(entity) {
        this.id = entity.id
        this.value = entity.name
        this.checked = entity.checked || false
    }
}

const EntityUtilityService = {
    /**
     * if current time is before the window --> all fine
     * if current time is after the window --> all timeslots until the end of the window are nonBookable
     * @param timeSlotStartTime
     * @param currentTime
     * @param entity
     * @return {boolean|*}
     */

    validateBookingWindow (timeSlotStartTime, currentTime, entity, end = undefined) {
        if (KeycloakService.isBookingAgent() && entity.upfrontBookingWindowInMinutes != 0) {

            let endMoment = undefined
            if (!end) {
                endMoment = moment().set({
                    hours: Number(entity.timeSlotEndTime.split(':')[0]),
                    minutes: Number(entity.timeSlotEndTime.split(':')[1]),
                    seconds: 0, milliseconds: 0
                })
            } else {
                endMoment = end
            }

            let startMoment = moment(endMoment).subtract(entity.upfrontBookingWindowInMinutes, 'minutes').startOf('day')
            let days = Math.abs(endMoment.diff(startMoment, 'days')) // calculate the number of different days


            let windowEndTime = moment(currentTime)
            windowEndTime.add(days, 'days')
                .set({hours: end ? end.hours : Number(entity.timeSlotEndTime.split(':')[0]),
                    minutes: end ? end.minutes : Number(entity.timeSlotEndTime.split(':')[1]),
                    seconds: 0, milliseconds: 0 })
            let windowStartTime = moment(windowEndTime)
            windowStartTime.subtract(entity.upfrontBookingWindowInMinutes, 'minutes')

            // check if current time is before window time
            if (currentTime.isSameOrAfter(windowStartTime)) {
                if ((timeSlotStartTime.isBetween(windowStartTime, windowEndTime) || timeSlotStartTime.isSame(windowStartTime))){
                    return false
                }
            }
            // check if this timeslot is between the window but on another day (in that window)
            if (!timeSlotStartTime.isSame(windowEndTime, 'day') && timeSlotStartTime.isBetween(windowStartTime, windowEndTime)) {
                return false
            }
            // If there is a booking window, you can never book on the same day
            if (timeSlotStartTime.isSame(windowStartTime, 'day') ) {
                return false
            }

            return true
        } else {
            return true
        }
    },

}

const EntityService = {

    getEntities: async function(locationId, startDate, endDate = undefined, customerId = undefined) {
        try {
            let startMoment = moment(startDate) // keep the old start date
            // we have to add padding to the window, since it can happen that the corrected bookings shift a day because of the timezone
            // for instance utc -6 booking at 20:00 corrected if 02:00 the next day
            startDate = moment(startDate).subtract(1, "day").startOf('day').toISOString()
            let endDateWindow = endDate
            if (endDate == undefined) {
               endDateWindow = moment(startDate).add(3, 'day').startOf('day').toISOString()
            } else {
                endDateWindow = moment(endDate).add(3, 'day').endOf('day').toISOString()

            }
            const customerQuery = customerId ? `&customerid=${customerId}` : ''

            const endpoint = `${Constants.ENDPOINT_ENTITIES}?locationid=${locationId}&startdate=${startDate}&enddate=${endDateWindow}${customerQuery}`
            const response = await ApiService.get(endpoint)
            // correct timezone
            let location = await LocationService.getLocation(locationId) //get the location (used to get the offset)
            let entities = response.data
            for (let i = 0; i < entities.length; i++) { // for every entity
                let bookings = entities[i].bookings // get the bookings
                for (let j = 0; j < bookings.length; j++) { // for every booking
                    bookings[j] = TimezoneService.correctTimezoneDifferenceDownEntities(bookings[j], location) // correct the start and end time
                }
                entities[i].bookings = bookings.filter(b => startMoment.isSame(moment(b.startDate), 'day')) // upate all the bookings for the startDate
            }

            return entities
        }catch(error) {
            throw new ApiError(error)
        }
    },

    postEntity: async function(entity, groupId) {
        let payload = entity
        let group = {"id": groupId.toString()}
        payload.group = group

        delete payload.bookings
        delete payload.startDate
        delete payload.endDate

        payload.timeSlotLengthInMinutes = Number(entity.timeSlotLengthInMinutes)
        payload.timeSlotToleranceInMinutes = Number(entity.timeSlotToleranceInMinutes)
        payload.reservationExpirationInMinutes = Number(entity.reservationExpirationInMinutes)
        payload.upfrontBookingWindowInMinutes = Number(entity.upfrontBookingWindowInMinutes)

        if (payload.description == undefined) {
            payload.description = ""
        }
        try {
            const response = await ApiService.post(Constants.ENDPOINT_ENTITIES, payload)
            return response
        } catch (e) {
            throw new ApiError(e)
        }
    },

    putEntity: async function(entity, groupId) {
        let payload = entity
        let group = {"id": groupId.toString()}
        payload.group = group

        delete payload.bookings
        delete payload.startDate
        delete payload.endDate

        payload.timeSlotLengthInMinutes = Number(entity.timeSlotLengthInMinutes)
        payload.timeSlotToleranceInMinutes = Number(entity.timeSlotToleranceInMinutes)
        payload.reservationExpirationInMinutes = Number(entity.reservationExpirationInMinutes)
        payload.upfrontBookingWindowInMinutes = Number(entity.upfrontBookingWindowInMinutes)


        if (payload.description == undefined) {
            payload.description = ""
        }

        try {
            let endpoint = Constants.ENDPOINT_ENTITIES + "/" + payload.id
            const response = await ApiService.put(endpoint, payload)
            return response
        } catch (e) {
            throw new ApiError(e)
        }
    },

    getEntityById: async function(id) {
        try {
            let response = await ApiService.get(Constants.ENDPOINT_ENTITIES + '/' + id)
            return response.data
        } catch (e) {
            throw new ApiError(e)
        }
    },

    postBookingOption: async function(entityId, entityOption) {
        try {
            let response = await ApiService.post(Constants.ENDPOINT_ENTITIES + '/' + entityId + '/booking-options', entityOption)
            return response.data
        } catch (e) {
            throw new ApiError(e)
        }
    },

    deleteOption: async function(entityId, optionId) {
        try {
            let response = await ApiService.delete(Constants.ENDPOINT_ENTITIES + '/' + entityId + '/booking-options/' + optionId)
            return response.data
        } catch (e) {
            throw new ApiError(e)
        }
    },

    getEntityOptionById: async function(entityId, optionId) {
        try {
            let response = await ApiService.get(Constants.ENDPOINT_ENTITIES + '/' + entityId + '/booking-options/' + optionId)
            return response.data
        } catch (error) {
            throw new ApiError(error)
        }
    },

    putBookingOption: async function(entityId, entityOption) {
        try {
            let payload = entityOption
            if (payload.timeSlotLengthFormulas.length == 0) {
                payload.timeSlotLengthFormulas = undefined
            }
            let response = await ApiService.put(Constants.ENDPOINT_ENTITIES + '/' + entityId + '/booking-options/' + entityOption.id, payload)
            return response.data
        } catch (error) {
            throw new ApiError(error)
        }
    },

    /**
     * Function that finds booking that are violating with the (new) openingHours
     * @param location
     * @return {Promise<*|*[]>}
     */
    findViolatingBookings: async function(location, entity, openingHours) {
        let entities = await EntityService.getEntities(location.id, moment().toDate(), moment().add(10, 'years')) // get all the bookings for all the entities
        let dayMapping = [6, 0, 1, 2, 3, 4, 5] // translate moment js days to format chooses in the ADT openingHours
        let bookings = []

        // extract all the bookings from the entities
        for (let e of entities) {
            if (e.id == entity.id) {
                for (let booking of e.bookings) {
                    // add fields needed in the SortedTable
                    booking.entity = e.name
                    booking.date = moment(booking.startDate).toDate().toLocaleDateString()
                    const startTime = moment(booking.startDate).format('HH:mm')
                    const endTime = moment(booking.endDate).format('HH:mm')
                    booking.timeslot = `${startTime} - ${endTime}`
                    bookings.push(booking)
                }
            }
        }

        let violations = []
        for (let booking of bookings) {
            let startdate = booking.startDate
            let startMoment = moment(startdate) // translate the booking start date to moment

            let enddate = booking.endDate
            let endMoment = moment(enddate) // translate the booking end date to moment

            let dayIndex = dayMapping[moment(startdate).day()]
            let openingTimes = openingHours.defaultWeek[dayIndex].times // get the closing times
            for (let interval of openingTimes) { // for every interval check
                let startInterval = moment(startdate).set({
                    hour: Number(interval.from.split(':')[0]),
                    minutes: Number(interval.from.split(':')[1]),
                    seconds: 0, milliseconds: 0
                })


                let endInterval = moment(enddate).set({
                    hour: Number(interval.to.split(':')[0]),
                    minutes: Number(interval.to.split(':')[1]),
                    seconds: 0, milliseconds: 0
                })

                if (startInterval.isSameOrBefore(startMoment) && endInterval.isAfter(endMoment)) { // if the booking is between the closing times
                    violations.push(booking)
                }
            }
            // find the bookings during the exception
            let exceptions = openingHours.exceptions // get the exceptions
            for (let exception of exceptions) { // for evert exception
                let exceptionMoment = moment(new Date(exception.date))
                if (startMoment.isSame(exceptionMoment, 'date')) { // if the exception is on the same date of the booking
                    let times = exception.times // get the exception times
                    for (let interval of times) {
                        let startInterval = moment(startdate).set({
                            hour: Number(interval.from.split(':')[0]),
                            minutes: Number(interval.from.split(':')[1]),
                            seconds: 0, milliseconds: 0
                        })


                        let endInterval = moment(enddate).set({
                            hour: Number(interval.to.split(':')[0]),
                            minutes: Number(interval.to.split(':')[1]),
                            seconds: 0, milliseconds: 0
                        })

                        if (startInterval.isSameOrBefore(startMoment) && endInterval.isAfter(endMoment)) { // check if the booking violates these times
                            violations.push(booking)
                        }
                    }
                }
            }
        }

        violations = _.uniq(violations) // only return the unique violations
        return violations
    }




}

export { EntityService, EntityUtilityService, Entity, EntityBooking, EntityOption, EntityDropdown, TimeSlotLengthDropdown}
